import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { ResumptionOverviewFormField } from '../../formField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'
import { AdapterOptions } from '../../../../../adapters/types'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { RocFormData } from '../../../../types/RocFormData'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Resumption Overview subsection adapter
 */
export class OverviewAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract date values and convert from UTC to caregiver timezone
    const rocDateUTC = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.RESUMPTION_OVERVIEW_OVERVIEW_ROC_DATE
    ).at(0)

    const inpatientRocDateUTC = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.RESUMPTION_OVERVIEW_OVERVIEW_INPATIENT_DISCHARGE_DATE
    ).at(0)

    // Convert UTC dates to caregiver timezone for form display
    const rocDate = rocDateUTC
      ? options?.caregiverTimezone
        ? dayjs.utc(rocDateUTC).tz(options.caregiverTimezone)
        : dayjs.utc(rocDateUTC)
      : undefined

    const inpatientRocDate = inpatientRocDateUTC
      ? options?.caregiverTimezone
        ? dayjs.utc(inpatientRocDateUTC).tz(options.caregiverTimezone)
        : dayjs.utc(inpatientRocDateUTC)
      : undefined

    // Non-date fields don't need timezone conversion
    const reasonForInpatientStay = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.RESUMPTION_OVERVIEW_OVERVIEW_REASON_FOR_INPATIENT_STAY
    ).at(0)

    const inpatientRocInstructions = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.RESUMPTION_OVERVIEW_OVERVIEW_INPATIENT_DISCHARGE_INSTRUCTIONS
    ).at(0)

    const comments = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.RESUMPTION_OVERVIEW_OVERVIEW_COMMENTS
    ).at(0)

    return {
      [ResumptionOverviewFormField.OVERVIEW_ROC_DATE]: rocDate,
      [ResumptionOverviewFormField.OVERVIEW_INPATIENT_DISCHARGE_DATE]: inpatientRocDate,
      [ResumptionOverviewFormField.OVERVIEW_REASON_FOR_INPATIENT_STAY]: reasonForInpatientStay,
      [ResumptionOverviewFormField.OVERVIEW_INPATIENT_DISCHARGE_INSTRUCTIONS]:
        inpatientRocInstructions,
      [ResumptionOverviewFormField.OVERVIEW_COMMENTS]: comments
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    // Convert date values back to UTC for FHIR storage
    if (this.formDataFieldExists(ResumptionOverviewFormField.OVERVIEW_ROC_DATE)) {
      const rocDateValue = this.getFormDataField(ResumptionOverviewFormField.OVERVIEW_ROC_DATE)
      const rocDateUTC =
        rocDateValue && dayjs.isDayjs(rocDateValue) ? rocDateValue.utc().toISOString() : undefined

      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.RESUMPTION_OVERVIEW_OVERVIEW_ROC_DATE,
        value: rocDateUTC
      })
    }

    if (this.formDataFieldExists(ResumptionOverviewFormField.OVERVIEW_INPATIENT_DISCHARGE_DATE)) {
      const inpatientRocDateValue = this.getFormDataField(
        ResumptionOverviewFormField.OVERVIEW_INPATIENT_DISCHARGE_DATE
      )
      const inpatientRocDateUTC =
        inpatientRocDateValue && dayjs.isDayjs(inpatientRocDateValue)
          ? inpatientRocDateValue.utc().toISOString()
          : undefined

      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.RESUMPTION_OVERVIEW_OVERVIEW_INPATIENT_DISCHARGE_DATE,
        value: inpatientRocDateUTC
      })
    }

    if (this.formDataFieldExists(ResumptionOverviewFormField.OVERVIEW_REASON_FOR_INPATIENT_STAY)) {
      const reasonForInpatientStay = this.getFormDataField(
        ResumptionOverviewFormField.OVERVIEW_REASON_FOR_INPATIENT_STAY
      )

      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.RESUMPTION_OVERVIEW_OVERVIEW_REASON_FOR_INPATIENT_STAY,
        value: reasonForInpatientStay
      })
    }

    if (
      this.formDataFieldExists(
        ResumptionOverviewFormField.OVERVIEW_INPATIENT_DISCHARGE_INSTRUCTIONS
      )
    ) {
      const inpatientRocInstructions = this.getFormDataField(
        ResumptionOverviewFormField.OVERVIEW_INPATIENT_DISCHARGE_INSTRUCTIONS
      )

      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.RESUMPTION_OVERVIEW_OVERVIEW_INPATIENT_DISCHARGE_INSTRUCTIONS,
        value: inpatientRocInstructions
      })
    }

    if (this.formDataFieldExists(ResumptionOverviewFormField.OVERVIEW_COMMENTS)) {
      const comments = this.getFormDataField(ResumptionOverviewFormField.OVERVIEW_COMMENTS)

      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.RESUMPTION_OVERVIEW_OVERVIEW_COMMENTS,
        value: comments
      })
    }

    return mutations
  }
}
